<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";

/**
 * Full-page or section loading indicator. Matches the page-empty pattern:
 * centered icon tile + title + description. The icon defaults to a spinning
 * loader but can be overridden for context-specific loading states.
 */
const props = withDefaults(
  defineProps<{
    /** Custom icon name; defaults to a loader. An empty value hides the icon. */
    icon?: string | null;
    /** Loading title; defaults to friendly message */
    title?: string;
    /** Optional description text */
    description?: string;
    /** Show the bordered background tile around the icon. */
    tile?: boolean;
    /** Rotate the icon. */
    spin?: boolean;
    /** Pulse the icon tile. */
    pulse?: boolean;
    /** Show the expanding ring behind the icon. */
    ping?: boolean;
    /** Additional classes for the container */
    class?: HTMLAttributes["class"];
  }>(),
  {
    title: "Loading...",
    tile: true,
    spin: true,
    pulse: true,
    ping: true,
  }
);

const resolvedIcon = computed(() =>
  props.icon === undefined ? "lucide:loader-circle" : (props.icon ?? "").trim(),
);
const hasIcon = computed(() => resolvedIcon.value.length > 0);
</script>

<template>
  <div
    data-slot="loading-indicator"
    :class="cn('flex flex-col items-center justify-center rounded-lg py-16 text-center', props.class)"
  >
    <div
      v-if="hasIcon"
      class="relative mb-4 flex size-14 items-center justify-center"
      :class="[
        props.tile && 'rounded-xl border bg-muted/30',
        { 'animate-pulse': props.pulse },
      ]"
    >
      <div
        v-if="props.ping"
        class="absolute border-2 border-primary/20 animate-ping"
        :class="props.tile ? 'inset-0 rounded-xl' : 'inset-2 rounded-full'"
        style="animation-duration: 2s"
      />
      <Icon
        mode="svg"
        :size="28"
        :name="resolvedIcon"
        class="text-primary"
        :class="{ 'animate-spin': props.spin }"
        :style="props.spin ? 'animation-duration: 1.2s' : undefined"
      />
    </div>

    <h3 v-if="props.title" class="text-base font-semibold text-foreground">
      {{ props.title }}
    </h3>
    <p v-if="props.description" class="mt-1 max-w-[44ch] text-sm text-muted-foreground">
      {{ props.description }}
    </p>

    <!-- Optional action slot for cancel buttons, etc. -->
    <div v-if="$slots.action" class="mt-5">
      <slot name="action" />
    </div>
  </div>
</template>
